/**
 * Web Bluetooth singleton — EventTarget-based, survives React re-renders.
 *
 * Events emitted:
 *   'statechange'   — ConnectionState changed
 *   'spectrum'      — New SpectrumFrame received
 *   'alert'         — New Alert received
 *   'error'         — BLE error occurred
 */

import type { ConnectionState, BLEConnectionInfo } from '../types/ble';
import {
  BLE_SERVICE_UUID,
  CHAR_ALERT_UUID,
  CHAR_SPECTRUM_UUID,
} from '../types/ble';
import { parseAlertPacket, parseSpectrumData } from './ble-protocol';
import {
  SPECTRUM_POLL_MS,
  RECONNECT_BASE_MS,
  RECONNECT_MAX_MS,
  RECONNECT_FACTOR,
  RECONNECT_JITTER,
} from './constants';
import type { Alert } from '../types/alerts';
import type { SpectrumFrame } from '../types/spectrum';

// Custom event types
export class SpectrumEvent extends Event {
  constructor(public readonly frame: SpectrumFrame) {
    super('spectrum');
  }
}

export class AlertEvent extends Event {
  constructor(public readonly alert: Alert) {
    super('alert');
  }
}

export class StateChangeEvent extends Event {
  constructor(public readonly info: BLEConnectionInfo) {
    super('statechange');
  }
}

export class BLEErrorEvent extends Event {
  constructor(public readonly error: string) {
    super('error');
  }
}

class BLEManager extends EventTarget {
  private device: BluetoothDevice | null = null;
  private server: BluetoothRemoteGATTServer | null = null;
  private alertChar: BluetoothRemoteGATTCharacteristic | null = null;
  private spectrumChar: BluetoothRemoteGATTCharacteristic | null = null;
  private spectrumTimer: ReturnType<typeof setTimeout> | null = null;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private reconnectAttempts = 0;
  private _state: ConnectionState = 'disconnected';
  private _error: string | null = null;
  private intentionalDisconnect = false;

  get state(): ConnectionState {
    return this._state;
  }

  get connectionInfo(): BLEConnectionInfo {
    return {
      state: this._state,
      device: this.device,
      error: this._error,
      rssi: null,
      reconnectAttempts: this.reconnectAttempts,
    };
  }

  get isSupported(): boolean {
    return typeof navigator !== 'undefined' && 'bluetooth' in navigator;
  }

  private setState(state: ConnectionState, error?: string) {
    this._state = state;
    this._error = error ?? null;
    this.dispatchEvent(new StateChangeEvent(this.connectionInfo));
  }

  /** Request device and connect */
  async connect(): Promise<void> {
    if (!this.isSupported) {
      this.setState('error', 'Web Bluetooth not supported');
      return;
    }

    this.intentionalDisconnect = false;
    this.reconnectAttempts = 0;

    try {
      this.setState('scanning');

      this.device = await navigator.bluetooth.requestDevice({
        filters: [{ services: [BLE_SERVICE_UUID] }],
        optionalServices: [BLE_SERVICE_UUID],
      });

      this.device.addEventListener('gattserverdisconnected', this.onDisconnected);
      await this.connectToServer();
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Connection failed';
      // User cancelled the scan dialog — not an error
      if (msg.includes('cancelled') || msg.includes('canceled')) {
        this.setState('disconnected');
      } else {
        this.setState('error', msg);
      }
    }
  }

  private async connectToServer(): Promise<void> {
    if (!this.device?.gatt) {
      this.setState('error', 'No GATT server');
      return;
    }

    this.setState('connecting');

    try {
      this.server = await this.device.gatt.connect();
      const service = await this.server.getPrimaryService(BLE_SERVICE_UUID);

      // Get characteristics
      this.alertChar = await service.getCharacteristic(CHAR_ALERT_UUID);
      this.spectrumChar = await service.getCharacteristic(CHAR_SPECTRUM_UUID);

      // Subscribe to alert notifications
      await this.alertChar.startNotifications();
      this.alertChar.addEventListener(
        'characteristicvaluechanged',
        this.onAlertNotification,
      );

      // Start spectrum polling via setTimeout chain
      this.startSpectrumPolling();

      this.reconnectAttempts = 0;
      this.setState('connected');
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Connection failed';
      this.setState('error', msg);
    }
  }

  private onDisconnected = () => {
    this.stopSpectrumPolling();

    if (this.intentionalDisconnect) {
      this.setState('disconnected');
      return;
    }

    // Attempt reconnection with exponential backoff + jitter
    this.setState('reconnecting');
    this.scheduleReconnect();
  };

  private scheduleReconnect(): void {
    const baseDelay = Math.min(
      RECONNECT_BASE_MS * Math.pow(RECONNECT_FACTOR, this.reconnectAttempts),
      RECONNECT_MAX_MS,
    );
    const jitter = baseDelay * RECONNECT_JITTER * (Math.random() * 2 - 1);
    const delay = Math.max(0, baseDelay + jitter);

    this.reconnectTimer = setTimeout(async () => {
      this.reconnectAttempts++;
      try {
        await this.connectToServer();
      } catch {
        if (!this.intentionalDisconnect) {
          this.scheduleReconnect();
        }
      }
    }, delay);
  }

  private onAlertNotification = (event: Event) => {
    const target = event.target as BluetoothRemoteGATTCharacteristic;
    if (!target.value) return;

    const alert = parseAlertPacket(target.value);
    if (alert && alert.type !== 0) {
      this.dispatchEvent(new AlertEvent(alert));
    }
  };

  private startSpectrumPolling(): void {
    const poll = async () => {
      if (this._state !== 'connected' || !this.spectrumChar) return;

      try {
        const value = await this.spectrumChar.readValue();
        const frame = parseSpectrumData(value);
        if (frame) {
          this.dispatchEvent(new SpectrumEvent(frame));
        }
      } catch {
        // Read failed — will retry on next tick if still connected
      }

      // Schedule next poll via setTimeout (avoids setInterval drift)
      if (this._state === 'connected') {
        this.spectrumTimer = setTimeout(poll, SPECTRUM_POLL_MS);
      }
    };

    poll();
  }

  private stopSpectrumPolling(): void {
    if (this.spectrumTimer) {
      clearTimeout(this.spectrumTimer);
      this.spectrumTimer = null;
    }
  }

  /** Intentional disconnect */
  disconnect(): void {
    this.intentionalDisconnect = true;
    this.stopSpectrumPolling();

    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }

    if (this.alertChar) {
      this.alertChar.removeEventListener(
        'characteristicvaluechanged',
        this.onAlertNotification,
      );
    }

    if (this.device?.gatt?.connected) {
      this.device.gatt.disconnect();
    }

    this.device = null;
    this.server = null;
    this.alertChar = null;
    this.spectrumChar = null;
    this.setState('disconnected');
  }
}

/** Singleton — one BLE connection for the entire app */
export const bleManager = new BLEManager();
